import { isAbsoluteUrl } from '@models/classes/utilities';
import { Observable } from 'rxjs';

export enum eContentType {
    json = 'application/json',
    formData = 'multipart/form-data',
}

export interface IHttpEnvironment {
    lastDeploy?: number;
    cacheBuster?: string;
}

interface IDeferredAssetCall<T> {
    func: () => Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: any) => void;
    priority: number;
}

export interface IBinaryResponse<T> {
    data: T;
    width?: number;
    height?: number;
}

export const ABORTED = 'ABORTED';

export class HttpService {
    public trackedAssetCalls = new Array<Promise<any>>();
    private deferredAssetCalls = new Array<IDeferredAssetCall<any>>();
    private httpCallsToAbort = new Map<string, string[]>();
    private deferredExecuted = false;

    constructor(private environment?: IHttpEnvironment) {}

    public addCacheParams(url: string): string {
        return this.getUrlWithParams(url, this.getEnvironmentCacheParams());
    }

    public get<T = any>(url: string, params?: Record<string, any>, trackAssetCall = true) {
        if (!isAbsoluteUrl(url) && this.environment) {
            // api calls in the test and prod environments should send the last deploy,
            // which allows the browser cache to store results for some calls
            if (this.environment) {
                const addParams = this.getEnvironmentCacheParams();
                params = params ? { ...params, ...addParams } : addParams;
            }
        }

        const u = this.getUrlWithParams(url, params);
        const p = this.fetchWrapper<T>(u, 'GET');
        if (trackAssetCall) {
            this.trackAssetCall(p);
        }
        return p;
    }

    public getPublicStatic<T = any>(
        url: string,
        type: 'json',
        params?: Record<string, any>,
        trackAssetCall?: boolean
    ): Promise<T>;
    public getPublicStatic(
        url: string,
        type: 'blob',
        params?: Record<string, any>,
        trackAssetCall?: boolean
    ): Promise<Blob>;
    public getPublicStatic(
        url: string,
        type: 'arraybuffer',
        params?: Record<string, any>,
        trackAssetCall?: boolean
    ): Promise<ArrayBuffer>;
    public getPublicStatic(
        url: string,
        type?: 'text',
        params?: Record<string, any>,
        trackAssetCall?: boolean
    ): Promise<string>;
    public getPublicStatic(
        url: string,
        type: 'text' | 'blob' | 'json' | 'arraybuffer' = 'text',
        params?: Record<string, any>,
        trackAssetCall = true
    ) {
        const u = this.getUrlWithParams(url, params);

        const requestInit = {
            method: 'GET',
            credentials: 'omit',
        } as RequestInit;
        const p = fetch(u, requestInit).then(res => {
            if (type === 'text') {
                return res.text();
            } else if (type === 'blob') {
                return res.blob();
            } else if (type === 'arraybuffer') {
                return res.arrayBuffer();
            } else {
                return res.json();
            }
        });

        if (trackAssetCall) {
            this.trackAssetCall(p);
        }
        return p;
    }

    public post<T = any>(url: string, body?: any, contentType = eContentType.json) {
        return this.fetchWrapper<T>(url, 'POST', body, contentType);
    }
    public put<T = any>(url: string, body?: any, contentType = eContentType.json) {
        return this.fetchWrapper<T>(url, 'PUT', body, contentType);
    }
    public delete<T = any>(url: string, params?: Record<string, any>) {
        const u = this.getUrlWithParams(url, params);
        return this.fetchWrapper<T>(u, 'DELETE');
    }

    getBinary(
        url: string,
        responseType: 'arraybuffer',
        trackAssetCall: boolean,
        appendCacheParameters?: boolean,
        abortSubject?: Observable<void>
    ): Promise<IBinaryResponse<ArrayBuffer>>;
    getBinary(
        url: string,
        responseType: 'blob',
        trackAssetCall: boolean,
        appendCacheParameters?: boolean,
        abortSubject?: Observable<void>
    ): Promise<IBinaryResponse<Blob>>;
    public getBinary(
        url: string,
        responseType: 'arraybuffer' | 'blob',
        trackAssetCall: boolean,
        appendCacheParameters = false,
        abortSubject?: Observable<void>
    ): Promise<IBinaryResponse<ArrayBuffer | Blob>> {
        const xhr = new XMLHttpRequest();
        if (appendCacheParameters && !isAbsoluteUrl(url) && this.environment) {
            if (this.environment) {
                const addParams = this.getEnvironmentCacheParams();
                url = this.getUrlWithParams(url, addParams);
            }
        }
        const abortSub = abortSubject?.subscribe(() => {
            xhr.abort();
        });

        const p = new Promise<IBinaryResponse<ArrayBuffer | Blob>>((resolve, reject) => {
            xhr.open('GET', url, true);
            xhr.responseType = responseType;

            xhr.onload = function (oEvent) {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const allHeaders = xhr.getAllResponseHeaders();
                    let height = null,
                        width = null;
                    if (allHeaders.match(/Original-Height/i)) {
                        height = xhr.getResponseHeader('X-Original-Height');
                        width = xhr.getResponseHeader('X-Original-Width');
                    }
                    const response: IBinaryResponse<ArrayBuffer | Blob> = {
                        data: xhr.response,
                    };
                    if (height && width) {
                        response.height = parseInt(height);
                        response.width = parseInt(width);
                    }
                    resolve(response);
                } else {
                    reject(xhr.statusText);
                }
                abortSub?.unsubscribe();
            };
            xhr.onerror = e => {
                reject(e);
                abortSub?.unsubscribe();
            };
            xhr.onabort = () => {
                reject(ABORTED);
                // do nothing
            };

            xhr.send(null);
        });

        if (trackAssetCall) {
            this.trackAssetCall(p);
        }

        return p;
    }

    /**
     * delays execution of parameter function until all blocking assets have been loaded for the scene.
     * executes immediately if scene is already loaded.
     */
    defer<T>(func: () => Promise<T>, priority = 100): Promise<T> {
        if (this.deferredExecuted) {
            return func();
        } else {
            return new Promise<T>((resolve, reject) => {
                this.deferredAssetCalls.push({ func, resolve, reject, priority });
            });
        }
    }

    executeDeferredCalls() {
        this.deferredAssetCalls.sort((a, b) => a.priority - b.priority);
        for (const call of this.deferredAssetCalls) {
            call.func().then(call.resolve).catch(call.reject);
        }
        this.deferredAssetCalls = [];
        this.deferredExecuted = true;
    }

    protected trackAssetCall(p: Promise<any>) {
        const trackedPromise = p.finally(() => {
            this.trackedAssetCalls.remove(trackedPromise);
        });
        this.trackedAssetCalls.push(trackedPromise);
    }

    private getEnvironmentCacheParams(): Record<string, string> {
        if (this.environment) {
            const params: Record<string, string> = {};
            if (this.environment.lastDeploy) {
                params.d = this.environment.lastDeploy.toString();
            }
            if (this.environment.cacheBuster) {
                params.v = this.environment.cacheBuster;
            }
            return params;
        }
        return {};
    }

    private getUrlWithParams(url: string, params?: Record<string, any>) {
        if (!params) return url;
        const urlParams = new URLSearchParams(Object.entries(params));

        let prefix = '?';
        if (url.match(/\?/)) {
            prefix = '&';
        }

        return `${url}${prefix}${urlParams.toString()}`;
    }

    private fetchWrapper<T>(
        url: string,
        method: string,
        body?: T,
        contentType: eContentType = eContentType.json
    ): Promise<T> {
        const headers: Headers = new Headers();

        const requestInit = {
            method,
            headers,
            credentials: 'include',
        } as RequestInit;

        if (method.equalsAny('POST', 'PUT')) {
            if (contentType == eContentType.json) {
                headers.append('Content-Type', contentType);
                requestInit.body = JSON.stringify(body);
            } else if (contentType == eContentType.formData) {
                requestInit.body = body as any as FormData;
            }
        }

        const p = fetch(url, requestInit).then(r => {
            if (r.ok) {
                return r.json().catch(() => body);
            } else {
                throw new Error(JSON.stringify(r));
            }
        });
        return p as Promise<T>;
    }
}
